import { useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { AssetAttachment } from "@torre/assets";

import { Button, Card, CardHeader } from "../../../components/ui";
import { confirmDelete } from "../../../components/ui/dialog";
import { useActivosMutation } from "../hooks/useActivos";
import { attachmentKindLabel, langOf } from "../lib/vocab";
import { activosApi, assetsErrorOf } from "../services/activosApi";

// ============================================================================
// Adjuntos de un activo o de una compra: factura PDF/XML y fotos (D11: se
// adjunta el archivo y el UUID se captura a mano). Storage sólo con URL
// firmada por el API. Archivo común, idéntico en CC-CONSOLE y WAYAK.
// ============================================================================

const ACCEPT = ".pdf,.xml,.png,.jpg,.jpeg,.webp,application/pdf,application/xml,text/xml,image/png,image/jpeg,image/webp";

export function AttachmentsPanel({
  target,
  attachments,
}: {
  target: { assetId: string } | { purchaseId: string };
  attachments: AssetAttachment[] | undefined;
}) {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const input = useRef<HTMLInputElement>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const upload = useActivosMutation((file: File) => activosApi.addAttachment(target, file));
  const remove = useActivosMutation((path: string) => activosApi.removeAttachment(target, path));
  const list = attachments || [];

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      await upload.mutateAsync(file);
      toast.success(t("attachments.uploaded"));
    } catch (err) {
      toast.error(assetsErrorOf(err).message || t("attachments.uploadError"));
    }
  };

  const open = async (path: string) => {
    setOpening(path);
    try {
      const { url } = await activosApi.attachmentUrl(path);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error(assetsErrorOf(err).message || t("attachments.openError"));
    } finally {
      setOpening(null);
    }
  };

  const onRemove = async (a: AssetAttachment) => {
    const ok = await confirmDelete({ title: t("attachments.removeTitle"), text: a.name, confirmText: t("attachments.remove"), cancelText: t("common.cancel") });
    if (!ok) return;
    try {
      await remove.mutateAsync(a.path);
    } catch (err) {
      toast.error(assetsErrorOf(err).message || t("attachments.removeError"));
    }
  };

  return (
    <Card>
      <CardHeader
        title={t("attachments.title")}
        subtitle={t("attachments.subtitle")}
        actions={
          <>
            <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={onPick} data-testid="attachment-input" />
            <Button size="sm" variant="secondary" icon={<i className="ri-upload-2-line" />} loading={upload.isPending} onClick={() => input.current?.click()}>
              {t("attachments.add")}
            </Button>
          </>
        }
      />
      {list.length === 0 ? (
        <p className="py-3 text-sm text-content-muted">{t("attachments.empty")}</p>
      ) : (
        <ul className="divide-y divide-line">
          {list.map((a) => (
            <li key={a.path} className="flex items-center gap-3 py-2">
              <i className={`${a.kind === "foto" ? "ri-image-line" : "ri-file-text-line"} text-lg text-content-muted`} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-content">{a.name}</p>
                <p className="text-xs text-content-faint">{attachmentKindLabel(a.kind, lang)}</p>
              </div>
              <Button size="sm" variant="ghost" iconOnly aria-label={t("attachments.open")} loading={opening === a.path} icon={<i className="ri-external-link-line" />} onClick={() => void open(a.path)} />
              <Button size="sm" variant="dangerSoft" iconOnly aria-label={t("attachments.remove")} icon={<i className="ri-delete-bin-line" />} onClick={() => void onRemove(a)} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
